import React, { useState } from "react"
import AdminButton from "../../../core/admin/adminButton";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminModal from "../../../core/admin/adminModal";
import AdminCard from "../../../core/admin/adminCard";
import AdminDetails from "../../../core/admin/adminDetails";
import AdminBadge from "../../../core/admin/adminBadge";
import AdminCallout from "../../../core/admin/adminCallout";
import AdminConfirm from "../../../core/admin/adminConfirm";
import { collection, getDocs, getCountFromServer, query, where, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import { useAuth } from "../../../../context/authProvider";
import { logAdminAction } from "../../../../utils/adminLog";
import { FileTextIcon, LockIcon, MailLineIcon, PhoneLineIcon, SearchLineIcon, WatchLineIcon } from "../../../../img/svg/adminIcons";
import AdminToast from "../../../core/admin/adminToast";
import { formatPhoneNumber, generateUniqueSlug } from "../../../../utils/functions";

interface IUser {
    id: string | undefined;
    name: string;
    email: string;
    slug?: string;
    roles: string[];
    phoneNumber: string;
    cpf?: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
}

export default function FindUser() {

    const { user: actor, userId: actorId } = useAuth();

    const [email, setEmail] = useState("");
    const [modalUser, setModalUser] = useState(false);
    const [userLoading, setUserLoading] = useState(false);
    const [operationLoading, setOperationLoading] = useState(false);
    const [foundUser, setFoundUser] = useState<IUser | null>(null);
    const [userFeedback, setUserFeedback] = useState("");
    const [handleAdmin, setHandleAdmin] = useState(false);
    const [handleAdvertiser, setHandleAdvertiser] = useState(false);
    const [handleEmailVerified, setHandleEmailVerified] = useState(false);
    const [handlePhoneVerified, setHandlePhoneVerified] = useState(false);
    const [goodToast, setGoodToast] = useState(false);
    const [badToast, setBadToast] = useState(false);
    // o toast de erro era sempre "Erro na operação!"; agora cada recusa explica o motivo
    const [badToastMessage, setBadToastMessage] = useState("Erro na operação!");

    const isMaster = !!foundUser?.roles?.includes("master");

    const failWith = (message: string) => {
        setBadToastMessage(message);
        setBadToast(true);
    }

    // dados de quem executou a ação, gravados junto do log de auditoria
    const actorFields = {
        actorId: actorId,
        actorName: actor?.name || null,
        actorEmail: actor?.email || null
    }

    const searchByEmail = async () => {
        try {
            setUserLoading(true);
            setUserFeedback("");
            const col = collection(db, "users");
            const q = query(col, where("email", "==", email));
            const documents = await getDocs(q);
            const document = documents.docs.find(d => d.data().email === email);
            const data = document?.data();
            if (data) {
                setFoundUser({ id: document?.id, name: data.name, email: data.email, roles: data.roles, phoneNumber: data.phoneNumber, slug: data.slug, cpf: data.cpf, emailVerified: data.emailVerified, phoneVerified: data.phoneVerified });
                setHandleEmailVerified(false);
                setHandlePhoneVerified(false);
                setModalUser(true);
            } else {
                setUserFeedback("Usuário não encontrado");
            }
            setUserLoading(false);
        } catch (error) {
            console.log(error)
            setUserLoading(false);
            failWith("Erro na busca!");
        }
    }

    const updateEmailVerified = async () => {
        if (foundUser?.id) {
            try {
                setOperationLoading(true);
                const userDoc = doc(db, "users", foundUser.id);
                await updateDoc(userDoc, {
                    emailVerified: true
                })
                await logAdminAction({
                    ...actorFields,
                    action: "verify.email",
                    targetId: foundUser.id,
                    targetName: foundUser.name,
                    targetEmail: foundUser.email,
                    details: "E-mail marcado como verificado sem envio de link"
                });
                setFoundUser({ ...foundUser, emailVerified: true });
                setHandleEmailVerified(false);
                setGoodToast(true);
                setOperationLoading(false);
            } catch (error) {
                console.log(error);
                setOperationLoading(false);
                failWith("Erro na operação!");
            }
        }
    }

    const updatePhoneVerified = async () => {
        if (foundUser?.id) {
            try {
                setOperationLoading(true);
                // sem telefone cadastrado, marcar como verificado deixaria o usuário travado:
                // updateUserPhone recusa alterações depois de phoneVerified
                if (!foundUser.phoneNumber) {
                    failWith("Usuário não tem telefone cadastrado.");
                    setOperationLoading(false);
                    setHandlePhoneVerified(false);
                    return null;
                }
                const userDoc = doc(db, "users", foundUser.id);
                await updateDoc(userDoc, {
                    phoneVerified: true
                })
                await logAdminAction({
                    ...actorFields,
                    action: "verify.phone",
                    targetId: foundUser.id,
                    targetName: foundUser.name,
                    targetEmail: foundUser.email,
                    details: `Telefone ${foundUser.phoneNumber} marcado como verificado sem envio de SMS`
                });
                setFoundUser({ ...foundUser, phoneVerified: true });
                setHandlePhoneVerified(false);
                setGoodToast(true);
                setOperationLoading(false);
            } catch (error) {
                console.log(error);
                setOperationLoading(false);
                failWith("Erro na operação!");
            }
        }
    }

    const updateAdmin = async () => {
        if (foundUser?.id) {
            try {
                setOperationLoading(true);
                if (foundUser.roles.includes("master")) {
                    failWith("Conta master não pode ser alterada.");
                    setOperationLoading(false);
                    setHandleAdmin(false);
                    return null;
                }
                const isRemoving = foundUser.roles.includes("admin");
                if (isRemoving) {
                    // trava de auto-demoção: um admin não pode remover a própria role
                    if (foundUser.id === actorId) {
                        failWith("Você não pode remover o seu próprio acesso de admin.");
                        setOperationLoading(false);
                        setHandleAdmin(false);
                        return null;
                    }
                    // trava de lockout: sem admin nenhum, só o console do Firestore reverteria
                    const adminsCount = await getCountFromServer(
                        query(collection(db, "users"), where("roles", "array-contains", "admin"))
                    );
                    if (adminsCount.data().count <= 1) {
                        failWith("Este é o último admin do sistema.");
                        setOperationLoading(false);
                        setHandleAdmin(false);
                        return null;
                    }
                }
                let newRoles;
                if (!isRemoving) {
                    newRoles = [...foundUser.roles, "admin"];
                } else {
                    newRoles = foundUser.roles.filter(role => role !== "admin");
                }
                const userDoc = doc(db, "users", foundUser.id);
                await updateDoc(userDoc, {
                    roles: newRoles
                })
                await logAdminAction({
                    ...actorFields,
                    action: isRemoving ? "role.admin.revoke" : "role.admin.grant",
                    targetId: foundUser.id,
                    targetName: foundUser.name,
                    targetEmail: foundUser.email,
                    details: `Roles: [${foundUser.roles.join(", ")}] -> [${newRoles.join(", ")}]`
                });
                setFoundUser({ ...foundUser, roles: newRoles });
                setHandleAdmin(false);
                setGoodToast(true);
                setOperationLoading(false);
            } catch (error) {
                console.log(error);
                setOperationLoading(false);
                failWith("Erro na operação!");
            }
        }
    }

    const updateAdvertiser = async () => {
        if (foundUser?.id) {
            try {
                setOperationLoading(true);
                // mesma proteção do updateAdmin: conta master não é alterável por aqui
                if (foundUser.roles.includes("master")) {
                    failWith("Conta master não pode ser alterada.");
                    setOperationLoading(false);
                    setHandleAdvertiser(false);
                    return null;
                }
                let fields: { [key: string]: string | string[] } = {};
                if (!foundUser.slug) {
                    // criar um slug não utilizado ainda
                    fields["slug"] = await generateUniqueSlug();
                }
                const isRemoving = foundUser.roles.includes("advertiser");
                let newRoles;
                if (!isRemoving) {
                    newRoles = [...foundUser.roles, "advertiser"];
                } else {
                    newRoles = foundUser.roles.filter(role => role !== "advertiser");
                }
                fields["roles"] = newRoles;
                const userDoc = doc(db, "users", foundUser.id);
                await updateDoc(userDoc, fields);
                await logAdminAction({
                    ...actorFields,
                    action: isRemoving ? "role.advertiser.revoke" : "role.advertiser.grant",
                    targetId: foundUser.id,
                    targetName: foundUser.name,
                    targetEmail: foundUser.email,
                    details: `Roles: [${foundUser.roles.join(", ")}] -> [${newRoles.join(", ")}]`
                        + (fields["slug"] ? ` | slug gerado: ${fields["slug"]}` : "")
                });
                setFoundUser({ ...foundUser, roles: newRoles });
                setHandleAdvertiser(false);
                setGoodToast(true);
                setOperationLoading(false);
            } catch (error) {
                console.log(error);
                setOperationLoading(false);
                failWith("Erro na operação!");
            }
        }
    }

    const isAdmin = !!foundUser?.roles.includes("admin");
    const isAdvertiser = !!foundUser?.roles.includes("advertiser");

    return (
        <AdminCard title="Pesquisar usuário por e-mail" description="O e-mail precisa ser exatamente o cadastrado pelo usuário.">
            <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
                <AdminTextInput type="email" value={email} onChange={e => setEmail(e.target.value)} onEnter={searchByEmail} placeholder="usuario@email.com" />
                <AdminButton icon={<SearchLineIcon width="16px" />} loading={userLoading} disabled={email === ""} onClick={searchByEmail}>Pesquisar</AdminButton>
            </div>
            {
                userFeedback ?
                    <p className="mt-3 text-sm font-semibold text-red-600">{userFeedback}</p>
                    : null
            }
            {
                modalUser ?
                    <AdminModal close={() => setModalUser(false)} size="lg" title={foundUser?.name || "Usuário"} subtitle={foundUser?.email}>
                        <div className="flex flex-col gap-6">
                            <AdminDetails
                                columns={3}
                                items={[
                                    { label: "ID", value: <span className="font-mono text-xs">{foundUser?.id}</span> },
                                    ...(foundUser?.slug ? [{ label: "Slug", value: <span className="font-mono text-xs">{foundUser?.slug}</span> }] : []),
                                    ...(foundUser?.cpf ? [{ label: "CPF", value: foundUser?.cpf }] : []),
                                ]}
                            />

                            <section>
                                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-1">Contato e verificação</h3>
                                <div className="divide-y divide-gray-100">
                                    <UserRow icon={<MailLineIcon />} title="E-mail" value={foundUser?.email} ok={!!foundUser?.emailVerified} okLabel="Verificado" notOkLabel="Não verificado">
                                        {
                                            foundUser?.emailVerified ?
                                                null
                                                : handleEmailVerified ?
                                                    <AdminConfirm message="Marcar como verificado?" loading={operationLoading} onCancel={() => setHandleEmailVerified(false)} onConfirm={updateEmailVerified} />
                                                    :
                                                    <AdminButton size="sm" variant="secondary" disabled={operationLoading} onClick={() => setHandleEmailVerified(true)}>Validar e-mail</AdminButton>
                                        }
                                    </UserRow>
                                    <UserRow icon={<PhoneLineIcon />} title="Telefone" value={foundUser?.phoneNumber ? formatPhoneNumber(foundUser.phoneNumber) : "Nenhum telefone cadastrado"} ok={!!foundUser?.phoneVerified} okLabel="Verificado" notOkLabel="Não verificado">
                                        {
                                            foundUser?.phoneVerified ?
                                                null
                                                : !foundUser?.phoneNumber ?
                                                    <p className="text-xs font-semibold text-red-600">Usuário precisa cadastrar um telefone antes</p>
                                                    : handlePhoneVerified ?
                                                        <AdminConfirm message="Marcar como verificado?" loading={operationLoading} onCancel={() => setHandlePhoneVerified(false)} onConfirm={updatePhoneVerified} />
                                                        :
                                                        <AdminButton size="sm" variant="secondary" disabled={operationLoading} onClick={() => setHandlePhoneVerified(true)}>Validar telefone</AdminButton>
                                        }
                                    </UserRow>
                                </div>
                            </section>

                            <section>
                                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-1">Papéis de acesso</h3>
                                {
                                    isMaster ?
                                        <AdminCallout tone="gold" icon="lock" title="Conta master" className="my-3">
                                            A role <strong>master</strong> não tem botão de atribuição em lugar nenhum do painel —
                                            ela só pode ser adicionada ou removida manualmente no console do Firestore.
                                            Enquanto ela existir nesta conta, as roles de admin e anunciante ficam bloqueadas aqui.
                                        </AdminCallout>
                                        : null
                                }
                                <div className="divide-y divide-gray-100">
                                    <UserRow icon={<LockIcon />} title="Admin" value="Acesso completo ao painel administrativo" ok={isAdmin} okLabel="Ativo" notOkLabel="Inativo">
                                        {
                                            isMaster ?
                                                <p className="text-xs font-semibold text-amber-700">Bloqueado (conta master)</p>
                                                : foundUser?.id === actorId && isAdmin ?
                                                    <p className="text-xs font-semibold text-amber-700">Você não pode destituir a si mesmo</p>
                                                    : handleAdmin ?
                                                        <AdminConfirm danger={isAdmin} loading={operationLoading} onCancel={() => setHandleAdmin(false)} onConfirm={updateAdmin} />
                                                        :
                                                        <AdminButton size="sm" variant={isAdmin ? "danger" : "secondary"} disabled={operationLoading} onClick={() => setHandleAdmin(true)}>{isAdmin ? "Destituir admin" : "Tornar admin"}</AdminButton>
                                        }
                                    </UserRow>
                                    <UserRow icon={<WatchLineIcon />} title="Anunciante" value="Pode cadastrar e anunciar relógios" ok={isAdvertiser} okLabel="Ativo" notOkLabel="Inativo">
                                        {
                                            isMaster ?
                                                <p className="text-xs font-semibold text-amber-700">Bloqueado (conta master)</p>
                                                : handleAdvertiser ?
                                                    <AdminConfirm danger={isAdvertiser} loading={operationLoading} onCancel={() => setHandleAdvertiser(false)} onConfirm={updateAdvertiser} />
                                                    :
                                                    <AdminButton size="sm" variant={isAdvertiser ? "danger" : "secondary"} disabled={operationLoading} onClick={() => setHandleAdvertiser(true)}>{isAdvertiser ? "Destituir anunciante" : "Tornar anunciante"}</AdminButton>
                                        }
                                    </UserRow>
                                </div>
                            </section>

                            <p className="flex items-center gap-2 text-xs text-gray-500">
                                <FileTextIcon width="14px" />
                                <span>Todas as alterações desta tela ficam registradas em <strong>Logs</strong>.</span>
                            </p>
                        </div>
                    </AdminModal>
                    : null
            }
            <AdminToast toast={goodToast} setToast={setGoodToast} tone="success" message="Usuário atualizado!" />
            <AdminToast toast={badToast} setToast={setBadToast} tone="danger" message={badToastMessage} />
        </AdminCard>

    )
}

// linha de status do modal: ícone, título/valor, badge e a ação (ou confirmação) à direita
function UserRow({ icon, title, value, ok, okLabel, notOkLabel, children }: { icon: React.ReactNode, title: string, value?: React.ReactNode, ok: boolean, okLabel: string, notOkLabel: string, children?: React.ReactNode }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">{icon}</span>
                <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
                        {title}
                        <AdminBadge tone={ok ? "success" : "neutral"} dot>{ok ? okLabel : notOkLabel}</AdminBadge>
                    </p>
                    {
                        value ?
                            <p className="text-sm text-gray-500 break-all">{value}</p>
                            : null
                    }
                </div>
            </div>
            {
                children ?
                    <div className="sm:max-w-[60%] sm:shrink-0 [&>*]:w-full sm:[&>*]:w-auto">{children}</div>
                    : null
            }
        </div>
    )
}